import React, { useEffect } from 'react';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import ApiManager from '#/services/api/ApiManager';
import { header2ClassName, spotifyApiConfig } from '#/utils/constants';
import { TRootState } from '#/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  createActionHomeAddFeaturedPlaylists,
  createActionHomeAddNewReleasedAlbums,
  createActionHomeAddTopArtists,
  createActionHomeAddTopTracks,
  createActionHomeErrorFeaturedPlaylists,
  createActionHomeErrorNewReleasedAlbums,
  createActionHomeErrorTopArtists,
  createActionHomeErrorTopTracks,
  createActionHomeLoadingFeaturedPlaylists,
  createActionHomeLoadingNewReleasedAlbums,
  createActionHomeLoadingTopArtists,
  createActionHomeLoadingTopTracks,
} from '#/redux/actions/home';
import Header from '#/components/Form/Header';
import AlbumPreview from '#/components/Layout/AlbumPreview';
import { ITopArtistsResponse } from '#/types/API/topArtists';
import ArtistPreview from '#/components/Layout/ArtistPreview';
import { ITopTracksResponse } from '#/types/API/topTracks';
import PreviewLayout from '#/components/Layout/PreviewLayout';
import useViewport from '#/hooks/useViewport';
import { useNavigate } from 'react-router-dom';
import { ROUTE_NAMES } from '#/router/routes';
import { getItemsPerBreakpoint } from '#/utils/getItemsPerBreakpoint';
import TopItemPreview from '#/components/Layout/TopItemPreview/TopItemPreview';
import useProfile from '#/hooks/useProfile';
import PlaylistPreview from '#/components/Layout/PlaylistPreview/PlaylistPreview';
import { convert } from 'html-to-text';
import {
  emptyFeaturedPlaylistsResponse,
  emptyNewReleasesResponse,
  okResponse,
} from '#/utils/spotifyApiHelpers';

const homeSelector = (state: TRootState) => {
  return state.home;
};

const Home = () => {
  const profile = useProfile();

  const { viewportWidth } = useViewport();

  const homeState = useSelector(homeSelector);

  const { newReleasedAlbums, topArtists, topTracks, featuredPlaylists } =
    homeState;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const topTracksLimit = 6;

  const topArtistLimit = 6;

  const navigate = useNavigate();

  useEffect(() => {
    const getNewReleasedAlbums = async () => {
      dispatch(createActionHomeLoadingNewReleasedAlbums());

      const response = await okResponse(emptyNewReleasesResponse);

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionHomeErrorNewReleasedAlbums());
      } else {
        dispatch(
          createActionHomeAddNewReleasedAlbums(response.data.albums.items)
        );
      }
    };

    const getTopArtists = async () => {
      dispatch(createActionHomeLoadingTopArtists());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<ITopArtistsResponse>(
        'topArtists',
        { limit: topArtistLimit }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionHomeErrorTopArtists());
      } else {
        dispatch(createActionHomeAddTopArtists(response.data.items));
      }
    };

    const getTopTracks = async () => {
      dispatch(createActionHomeLoadingTopTracks());
      const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

      const response = await apiManager.getAllEntities<ITopTracksResponse>(
        'topTracks',
        { limit: topTracksLimit }
      );

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionHomeErrorTopTracks());
      } else {
        dispatch(createActionHomeAddTopTracks(response.data.items));
      }
    };

    const getFeaturedPlaylists = async () => {
      dispatch(createActionHomeLoadingFeaturedPlaylists());

      const response = await okResponse(emptyFeaturedPlaylistsResponse);

      if ('error' in response) {
        // TODO display error notification
        dispatch(createActionHomeErrorFeaturedPlaylists());
      } else {
        dispatch(createActionHomeAddFeaturedPlaylists(response.data));
      }
    };

    getNewReleasedAlbums();
    getTopArtists();
    getTopTracks();
    getFeaturedPlaylists();
  }, [dispatch, protectedSpotify]);

  return (
    <div className=" p-6">
      <Header type="h2" className=" font-bold mb-8">
        Welcome {profile?.display_name}
      </Header>
      <Header type="h2" className={header2ClassName}>
        {'message' in featuredPlaylists
          ? featuredPlaylists.message
          : 'Featured playlists'}
      </Header>
      <PreviewLayout
        error={'_error' in featuredPlaylists && featuredPlaylists._error}
      >
        {'playlists' in featuredPlaylists
          ? featuredPlaylists.playlists.items.map((featuredPlaylist, index) => {
              if (index >= getItemsPerBreakpoint(viewportWidth, 2)) {
                return;
              }

              const handleOnClick = () =>
                navigate(`${ROUTE_NAMES.PLAYLIST}/${featuredPlaylist.id}`);

              return (
                <PlaylistPreview
                  key={featuredPlaylist.id}
                  image={featuredPlaylist.images[0].url}
                  name={featuredPlaylist.name}
                  description={convert(featuredPlaylist.description, {
                    wordwrap: false,
                  })}
                  onClick={handleOnClick}
                  uri={featuredPlaylist.uri}
                />
              );
            })
          : '_loading' in featuredPlaylists &&
            [
              ...Array.from({
                length: getItemsPerBreakpoint(viewportWidth, 2),
              }).keys(),
            ].map((key) => {
              return <PlaylistPreview key={key} loading />;
            })}
      </PreviewLayout>
      <Header type="h2" className={header2ClassName}>
        Last releases
      </Header>
      <PreviewLayout
        error={'_error' in newReleasedAlbums && newReleasedAlbums._error}
      >
        {Array.isArray(newReleasedAlbums)
          ? newReleasedAlbums.map((newReleasedAlbum, index) => {
              if (index >= getItemsPerBreakpoint(viewportWidth, 2)) {
                return;
              }

              const artistName = newReleasedAlbum.artists
                .map((artist) => artist.name)
                .join(', ');

              const handleOnClick = () =>
                navigate(`${ROUTE_NAMES.ALBUM}/${newReleasedAlbum.id}`);

              return (
                <AlbumPreview
                  key={newReleasedAlbum.id}
                  image={newReleasedAlbum.images[0].url}
                  artistName={artistName}
                  albumName={newReleasedAlbum.name}
                  onClick={handleOnClick}
                  uri={newReleasedAlbum.uri}
                />
              );
            })
          : '_loading' in newReleasedAlbums &&
            [
              ...Array.from({
                length: getItemsPerBreakpoint(viewportWidth, 2),
              }).keys(),
            ].map((key) => {
              return <AlbumPreview key={key} loading />;
            })}
      </PreviewLayout>
      <Header type="h2" className={header2ClassName}>
        Your top artists
      </Header>
      <PreviewLayout error={'_error' in topArtists && topArtists._error}>
        {Array.isArray(topArtists)
          ? topArtists.map((topArtist, index) => {
              if (index >= getItemsPerBreakpoint(viewportWidth)) {
                return;
              }

              const handleClickArtist = () =>
                navigate(`${ROUTE_NAMES.ARTIST}/${topArtist.id}`);

              return (
                <ArtistPreview
                  onClick={handleClickArtist}
                  key={topArtist.id}
                  image={topArtist.images[0].url}
                  name={topArtist.name}
                  uri={topArtist.uri}
                />
              );
            })
          : '_loading' &&
            [
              ...Array.from({
                length: getItemsPerBreakpoint(viewportWidth),
              }).keys(),
            ].map((key) => {
              return <ArtistPreview key={key} loading />;
            })}
      </PreviewLayout>
      <Header type="h2" className={header2ClassName}>
        Your top tracks
      </Header>
      <PreviewLayout error={'_error' in topTracks && topTracks._error}>
        {Array.isArray(topTracks)
          ? topTracks.map((topTrack, index) => {
              if (index >= getItemsPerBreakpoint(viewportWidth)) {
                return;
              }

              const handleOnClick = () =>
                navigate(`${ROUTE_NAMES.TRACK}/${topTrack.id}`);

              return (
                <TopItemPreview
                  key={topTrack.id}
                  image={topTrack.album.images[0].url}
                  name={topTrack.name}
                  releaseYear={Number(topTrack.album.release_date.slice(0, 4))}
                  onClick={handleOnClick}
                  uri={topTrack.uri}
                />
              );
            })
          : '_loading' in topTracks &&
            [
              ...Array.from({
                length: getItemsPerBreakpoint(viewportWidth),
              }).keys(),
            ].map((key) => {
              return <TopItemPreview key={key} loading />;
            })}
      </PreviewLayout>
    </div>
  );
};

export default Home;
